import { DataAccess, HttpGetRequestOptions } from "../../../dataaccess/data.access";
import { RoleRights } from "./role.rights";
import { AccessLevel, AccessLevels } from "../../../pojo/Fls";
import { LoginService } from "../../../services/login.service";

/**
 * Static service for role-based rights configuration API calls
 * All methods are static and use DataAccess for HTTP requests
 */
export class RoleRightsConfigService {
    /**
     * Get all right definitions from the backend enum
     * @param category Optional category filter
     */
    public static async getRightDefinitions(category?: string): Promise<RoleRights.RightDefinition[]> {
        const params = category ? `?category=${encodeURIComponent(category)}` : "";
        const response = await DataAccess.get<DefinitionsResponse>(`/role/rights/definitions.json${params}`);
        return response.definitions;
    }

    /**
     * Get all role-right assignments grouped by role
     */
    public static async getAllRoleRights(): Promise<Record<number, RoleRights.RoleRightAssignment[]>> {
        const response = await DataAccess.get<AllRoleRightsResponse>(`/role/rights/all.json`);
        return response.roleRights;
    }

    /**
     * Update which roles have access to a specific right
     * @param rightName The right identifier
     * @param roleIds Array of role IDs that should have this right
     * @param accessLevel The access level to grant (defaults to "F")
     */
    public static async updateRolesForRight(
        rightName: string,
        roleIds: number[],
        accessLevel: AccessLevel = "F" as AccessLevel,
    ): Promise<void> {
        await DataAccess.put(`/role/rights.json`, {
            roleRights: {
                rightName,
                roleIds,
                accessLevel,
            },
        });
    }

    /**
     * Combined rights configuration (global role defaults). When {@code forOrgId} is set, the server
     * requires {@code SE_ORG_SECURITY} read access on that org (see {@code RoleRightConfigController}).
     * Server JSON (strip-root): {@code {"rights":[...]}} — see {@code RightsConfigResponse}.
     */
    public static async getRightsConfig(forOrgId?: number): Promise<RoleRights.RightWithRolesConfig[]> {
        const params = forOrgId != null ? DataAccess.params({ forOrgId }) : "";
        const path = params ? `/role/rights/config.json?${params}` : `/role/rights/config.json`;
        const response = await DataAccess.get<RightsConfigResponse>(path);
        return response.rights ?? [];
    }

    /**
     * Save roles for a single right
     * @param rightName The right identifier
     * @param roleAssignments Map of roleId to accessLevel
     */
    public static async saveRolesForRight(rightName: string, roleAssignments: Map<number, AccessLevel>): Promise<void> {
        // Extract role IDs that have non-"N" access
        const roleIds: number[] = [];
        let accessLevel: AccessLevel = AccessLevels.Full;

        roleAssignments.forEach((level, roleId) => {
            roleIds.push(roleId);
            accessLevel = level; // All roles get the same access level
        });

        await this.updateRolesForRight(rightName, roleIds, accessLevel);
    }

    public static async getMaxRights() {
        const response = await DataAccess.get<SeRightsResponse>(`/role/rights/check.json?includeMaxRights=true`);
        return AccessLevelResponse.asRecord(response.maxRights ?? []);
    }

    public static async getOrgRights(orgIds: number[]) {
        const response = await DataAccess.post<SeRightsResponse>(`/role/rights/check.json?request_method=get`, {
            post_get: { organization_id: orgIds.join("+") },
        });

        // Transform and merge org rights
        return Object.fromEntries(
            Object.entries(response.orgRights ?? {}).map(([orgId, orgRights]) => [
                orgId,
                AccessLevelResponse.asRecord(orgRights),
            ]),
        ) satisfies Record<number, RoleRights.RightsAccess>;
    }

    /** Response is a JSON array. Org-specific role right overrides (replaces global default when present). */
    public static async getOrgRoleRightOverrides(orgId: number): Promise<OrgRoleRightOverridePayload[]> {
        return await DataAccess.get<OrgRoleRightOverridePayload[]>(`/role/rights/org/${orgId}/overrides.json`);
    }

    /**
     * Upserts one or more org-specific role/right overrides in a single request.
     * Body is a JSON array: each row is {@link OrgRoleRightOverridePayload} (same org, multiple {@code contRoleId} values allowed).
     */
    public static async putOrgRoleRightOverrides(overrides: OrgRoleRightOverridePayload[]): Promise<void> {
        await DataAccess.put(`/role/rights/org/overrides.json`, overrides);
    }

    /**
     * Removes one or more overrides in a single request (restores global default per key).
     * Body is a JSON array of key objects ({@link OrgRoleRightOverrideKeyPayload}).
     */
    public static async deleteOrgRoleRightOverrides(keys: OrgRoleRightOverrideKeyPayload[]): Promise<void> {
        await DataAccess.delete(`/role/rights/org/overrides.json`, { data: keys });
    }

    public static async deleteOrgRoleRightOverride(
        orgId: number,
        contRoleId: number,
        rightName: string,
    ): Promise<void> {
        await this.deleteOrgRoleRightOverrides([{ orgId, contRoleId, rightName }]);
    }

    public static async getUserRoles(orgId: number, httpOptions?: HttpGetRequestOptions) {
        const loggedIn = await LoginService.getLoggedIn();
        if (!loggedIn) return [];

        const url = `/role/rights/userRoles.json?${DataAccess.params({ org_id: orgId })}`;
        return await DataAccess.get<number[]>(url, httpOptions);
    }
}

/** Matches {@code AcctRoleRightOverride} JSON ({@code orgId} for {@code acctId}). */
export interface OrgRoleRightOverridePayload {
    orgId: number;
    contRoleId: number;
    rightName: string;
    accessLevel: AccessLevel;
}

/** Keys for DELETE / org override removal (accessLevel not required). */
export type OrgRoleRightOverrideKeyPayload = Pick<OrgRoleRightOverridePayload, "orgId" | "contRoleId" | "rightName">;

interface RightsConfigResponse {
    rights: RoleRights.RightWithRolesConfig[];
}

interface DefinitionsResponse {
    definitions: RoleRights.RightDefinition[];
    count: number;
}

interface AllRoleRightsResponse {
    roleRights: Record<number, RoleRights.RoleRightAssignment[]>;
    roleCount: number;
}

/**
 * AccessLevel from backend (matches Java AccessLevel class)
 */
interface AccessLevelResponse {
    objectId: number | null;
    rightName: string;
    accessLevel: AccessLevel;
}

namespace AccessLevelResponse {
    export function asRecord(rights: AccessLevelResponse[]): RoleRights.RightsAccess {
        return rights.reduce((acc, curr) => {
            if (curr.rightName in RoleRights.RoleRightsList) {
                acc[curr.rightName as RoleRights.RoleRightsList] = curr.accessLevel;
            }
            return acc;
        }, {} as RoleRights.RightsAccess);
    }
}

/**
 * Response from getSeRights endpoint
 */
interface SeRightsResponse {
    flsValue: AccessLevel;
    maxRights?: AccessLevelResponse[];
    orgRights: Record<number, AccessLevelResponse[]>;
}
